import {
  buildUiHelpSnapshot,
  probeHelpTarget,
  type HelpTargetProbe,
  type UiHelpSnapshot,
} from '@heidi/agents';
import { voiceController } from '../controller';
import { useVoiceGuideStore } from './useVoiceGuideStore';
import { useVoiceHighlightStore } from './useVoiceHighlightStore';

export type HighlightMissReport = {
  probe: HelpTargetProbe;
  snapshot: UiHelpSnapshot;
  highlightedFallback: string | null;
};

/**
 * After the overlay fails to find a target: tell the agent why, send a
 * curated UI snapshot, and fall back to highlighting a present parent when
 * the miss is "menu not open yet".
 */
export const reportHighlightMiss = (requestedTarget: string): HighlightMissReport => {
  const probe = probeHelpTarget(requestedTarget);
  const snapshot = buildUiHelpSnapshot();
  const guide = useVoiceGuideStore.getState().guide;
  const step = guide?.steps[guide.stepIndex];

  let highlightedFallback: string | null = null;
  if (probe.fallbackTarget && probe.reason === 'needs_open') {
    highlightedFallback = probe.fallbackTarget;
    const current = useVoiceHighlightStore.getState().highlight;
    useVoiceHighlightStore.getState().setHighlight({
      target: probe.fallbackTarget,
      title: current?.title ?? step?.title ?? undefined,
      // Clinician callout uses intl highlightMissing; keep agent probe.explanation
      // on the guide_event only.
      body: current?.body,
      durationMs: current?.durationMs ?? 12_000,
      startedAt: Date.now(),
      showContinue: current?.showContinue ?? true,
    });
  }

  voiceController.sendGuideEvent({
    guideId: guide?.id ?? 'ui.highlight',
    stepId: step?.id,
    status: 'target_missing',
    title: guide?.title,
    target: requestedTarget,
    reason: probe.reason ?? 'not_in_dom',
    explanation: probe.explanation,
    requires: probe.requires,
    fallbackTarget: highlightedFallback,
    snapshot,
  });

  return { probe, snapshot, highlightedFallback };
};
